<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import { TSButtonBulkAction } from 'tsv2-library';
import { MenuOption } from 'tsv2-library/dist/src/components/v2/Menu/Menu.vue';

import CancelBorrowingTransaction from './Dialog/CancelBorrowingTransaction.vue';
import CancelExtensionRequest from './Dialog/CancelExtensionRequest.vue';
import DeclineExtensionRequest from './Dialog/DeclineExtensionRequest.vue';

import CancelReport from '@/components/common/Dialog/CancelReport.vue';
import { BorrowingTransactionRequest } from '@/types/borrowing.type';

const router = useRouter();

const selectedData = defineModel<BorrowingTransactionRequest[]>(
  'selectedData',
  { required: true },
);

const showCancelBorrowingTransaction = ref<boolean>(false);
const showDeclineExtensionRequest = ref<boolean>(false);
const showCancelExtensionRequest = ref<boolean>(false);
const showCancelReport = ref<boolean>(false);

const bulkOptions = computed<MenuOption[]>(() => {
  const isWaitingForApprovalnHandoverAll = selectedData.value.every(
    (datum) =>
      datum.status === 'Waiting for Approval' ||
      datum.status === 'Waiting for Handover',
  );

  const isOnBorrowingAll = selectedData.value.every(
    (datum) => datum.status === 'On Borrowing',
  );

  const isExtendApprovalAll = selectedData.value.every(
    (datum) => datum.status === 'Extend Approval',
  );

  const isRequestExtensionAll = selectedData.value.every(
    (datum) => datum.status === 'Request Extension',
  );

  const isCanCancelReportDamagedAll = selectedData.value.every(
    (datum) =>
      datum.status === 'Reported Damaged' &&
      datum.isUnderRepair === false &&
      datum.isReportDone !== true,
  );

  const isCanCancelReportMissingAll = selectedData.value.every(
    (datum) =>
      datum.status === 'Reported Missing' &&
      datum.isUnderTracking === false &&
      datum.isReportDone !== true,
  );

  const isSelectedManagerAll = selectedData.value.every(
    (datum) => datum.isManager,
  );

  const options: MenuOption[] = [
    {
      label: 'No Option Available',
      disabled: true,
      visible:
        !isOnBorrowingAll &&
        !isExtendApprovalAll &&
        !isRequestExtensionAll &&
        !isWaitingForApprovalnHandoverAll &&
        !isCanCancelReportDamagedAll &&
        !isCanCancelReportMissingAll &&
        !isSelectedManagerAll,
    },
    {
      label: 'Return',
      icon: 'arrow-go-back-line',
      visible: isOnBorrowingAll && isSelectedManagerAll,
      command: (): void => {
        sessionStorage.setItem(
          'assetBorrowReturn',
          JSON.stringify(selectedData.value.map((datum) => datum._id)),
        );

        router.push({
          name: 'AssetBorrowBorrowedReturnAssetView',
        });
      },
    },
    {
      label: 'Decline Request',
      icon: 'close',
      visible: isRequestExtensionAll && isSelectedManagerAll,
      danger: true,
      command: (): void => {
        showDeclineExtensionRequest.value = true;
      },
    },
    {
      label: 'Cancel Extension Request',
      icon: 'close',
      visible: isExtendApprovalAll && isSelectedManagerAll,
      danger: true,
      command: (): void => {
        showCancelExtensionRequest.value = true;
      },
    },
    {
      label: 'Request Extension',
      icon: 'extension-borrow',
      visible: isOnBorrowingAll && isSelectedManagerAll,
      command: (): void => {
        sessionStorage.setItem(
          'assetBorrowRequestExtension',
          JSON.stringify(selectedData.value.map((datum) => datum._id)),
        );

        router.push({
          name: 'AssetBorrowTransactionExtensionRequestView',
        });
      },
    },
    {
      label: 'Cancel Borrowing',
      icon: 'close',
      danger: true,
      visible: isWaitingForApprovalnHandoverAll && isSelectedManagerAll,
      command: (): void => {
        showCancelBorrowingTransaction.value = true;
      },
    },
    {
      label: 'Cancel Report',
      icon: 'close',
      visible:
        (isCanCancelReportDamagedAll || isCanCancelReportMissingAll) &&
        isSelectedManagerAll,
      danger: true,
      command: (): void => {
        showCancelReport.value = true;
      },
    },
  ];

  return options.filter((option) => option.visible);
});
</script>
<template>
  <CancelReport
    v-model:visible="showCancelReport"
    :selected-data
    table-name="borrow-detail-table"
  />
  <CancelExtensionRequest
    v-model:visible="showCancelExtensionRequest"
    :selected-data
  />
  <DeclineExtensionRequest
    v-model:visible="showDeclineExtensionRequest"
    :selected-data
  />
  <CancelBorrowingTransaction
    v-model:visible="showCancelBorrowingTransaction"
    :selected-data
  />

  <TSButtonBulkAction
    v-model:selected-data="selectedData"
    :options="bulkOptions"
    naming="Asset(s)"
    table-name="borrow-detail-table"
  />
</template>
